// CameraCard Component - 2ECOMP Fleet Management
// Card wrapper for camera player with device info and controls

import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Chip,
  Divider,
  Stack,
  Badge,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SpeedIcon from '@mui/icons-material/Speed';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import JessibucaPlayer from './JessibucaPlayer';
import ChannelSelector from './ChannelSelector';
import { CHANNEL_NAMES } from '../utils/cameraConstants';
import { useDeviceCameraStatus } from '../hooks/useCameraStatus';
import { useVideoCommand } from '../hooks/useVideoCommand';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'box-shadow 0.3s ease, transform 0.2s ease',
  '&:hover': {
    boxShadow: theme.shadows[8],
    transform: 'translateY(-2px)',
  },
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  paddingTop: '56.25%', // 16:9 aspect ratio
  backgroundColor: '#000',
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  overflow: 'hidden',
}));

const VideoWrapper = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});

const LiveBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  left: theme.spacing(1),
  zIndex: 10,
  height: 24,
  fontSize: '0.7rem',
  fontWeight: 700,
  backgroundColor: alpha(theme.palette.error.main, 0.9),
  color: theme.palette.error.contrastText,
  '& .MuiChip-icon': {
    fontSize: 10,
    marginLeft: 6,
  },
}));

const DeviceInfo = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const InfoChip = styled(Chip)(({ theme }) => ({
  height: 22,
  fontSize: '0.7rem',
  '& .MuiChip-icon': {
    fontSize: 14,
  },
}));

/**
 * CameraCard - Card component displaying camera player with device info
 *
 * @param {Object} props
 * @param {Object} props.device - Device object with id, name, etc.
 * @param {number} props.initialChannel - Initial camera channel
 * @param {boolean} props.autoPlay - Auto-start stream
 * @param {boolean} props.showDeviceInfo - Show device information bar
 * @param {boolean} props.showChannelSelector - Show channel selector
 * @param {boolean} props.compact - Compact mode (less padding)
 * @param {function} props.onOpenFullscreen - Callback to open in fullscreen view
 * @param {function} props.onOpenHistory - Callback to open recording history
 * @param {function} props.onOpenSettings - Callback to open device settings
 * @param {function} props.onDeviceClick - Callback when device name is clicked
 * @param {Object} props.position - Current device position data
 * @param {Object} props.sx - Additional MUI sx props
 */
const CameraCard = ({
  device,
  initialChannel = 1,
  autoPlay = false,
  showDeviceInfo = true,
  showChannelSelector = true,
  compact = false,
  onOpenFullscreen = null,
  onOpenHistory = null,
  onOpenSettings = null,
  onDeviceClick = null,
  position = null,
  sx = {},
}) => {
  const [selectedChannel, setSelectedChannel] = useState(initialChannel);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [streaming, setStreaming] = useState(autoPlay);

  // Get camera status for this device
  const { hasCamera, activeChannels, channelCount } = useDeviceCameraStatus(device?.id);
  const { requestVideo, stopVideo } = useVideoCommand();

  // Send JT1078 requestVideo/stopVideo when streaming state changes
  useEffect(() => {
    if (!device?.id || !streaming) return undefined;
    requestVideo(device.id, { channel: selectedChannel }).catch((err) =>
      console.warn('[CameraCard] requestVideo failed:', err));
    return () => {
      stopVideo(device.id, { channel: selectedChannel }).catch((err) =>
        console.warn('[CameraCard] stopVideo failed:', err));
    };
  }, [device?.id, selectedChannel, streaming, requestVideo, stopVideo]);

  const handleChannelChange = useCallback((channel) => {
    setSelectedChannel(channel);
  }, []);

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleStartStream = useCallback(() => {
    setStreaming(true);
  }, []);

  const handleStopStream = useCallback(() => {
    setStreaming(false);
  }, []);

  // Menu actions
  const menuActions = [
    {
      label: 'Abrir em Nova Janela',
      icon: <OpenInNewIcon fontSize="small" />,
      onClick: () => {
        handleMenuClose();
        if (onOpenFullscreen) onOpenFullscreen(device, selectedChannel);
      },
    },
    {
      label: 'Histórico de Gravações',
      icon: <HistoryIcon fontSize="small" />,
      onClick: () => {
        handleMenuClose();
        if (onOpenHistory) onOpenHistory(device, selectedChannel);
      },
    },
    {
      label: 'Configurações',
      icon: <SettingsIcon fontSize="small" />,
      onClick: () => {
        handleMenuClose();
        if (onOpenSettings) onOpenSettings(device);
      },
    },
  ];

  if (!device) {
    return (
      <StyledCard sx={sx}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <VideocamIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Nenhum dispositivo selecionado
          </Typography>
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <StyledCard sx={sx}>
      {/* Video Player Area */}
      <VideoContainer>
        <VideoWrapper>
          {streaming ? (
            <JessibucaPlayer
              deviceImei={device.uniqueId}
              channel={selectedChannel}
              autoplay
              onStop={handleStopStream}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#000',
                color: '#fff',
              }}
            >
              <VideocamOffIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
              <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
                Câmera desconectada
              </Typography>
              <IconButton onClick={handleStartStream} sx={{ color: '#fff' }}>
                <PlayArrowIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </Box>
          )}
        </VideoWrapper>

        {/* Live Badge */}
        {streaming && (
          <LiveBadge
            icon={<FiberManualRecordIcon />}
            label="AO VIVO"
            size="small"
          />
        )}
      </VideoContainer>

      {/* Device Info Bar */}
      {showDeviceInfo && (
        <DeviceInfo>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{
                fontWeight: 600,
                cursor: onDeviceClick ? 'pointer' : 'default',
                '&:hover': onDeviceClick ? { textDecoration: 'underline' } : {},
              }}
              onClick={() => onDeviceClick && onDeviceClick(device)}
            >
              {device.name}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {position?.speed !== undefined && (
                <InfoChip
                  icon={<SpeedIcon />}
                  label={`${Math.round(position.speed * 1.852)} km/h`}
                  size="small"
                  variant="outlined"
                />
              )}
              {channelCount > 0 && (
                <InfoChip
                  icon={<VideocamIcon />}
                  label={`${channelCount} câm.`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          <IconButton size="small" onClick={handleMenuOpen}>
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {menuActions.map((action, index) => (
              <MenuItem key={index} onClick={action.onClick}>
                <ListItemIcon>{action.icon}</ListItemIcon>
                <ListItemText>{action.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </DeviceInfo>
      )}

      {/* Channel Selector */}
      {showChannelSelector && (
        <CardContent sx={{ py: compact ? 1 : 1.5, px: compact ? 1.5 : 2 }}>
          <ChannelSelector
            selectedChannel={selectedChannel}
            activeChannels={activeChannels}
            onChange={handleChannelChange}
            compact={compact}
          />
        </CardContent>
      )}
    </StyledCard>
  );
};

export default CameraCard;
